import React, { FC, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Alert, Button } from '@clearcover/atlas';
import { CSSTransition } from 'react-transition-group';
import { useHistory } from 'react-router-dom';
import urlJoin from 'url-join';

import { Column, LoadingPage, Row } from 'Shared/components';
import { QUOTE_APP_ROUTE } from 'Shared/constants/routes';
import { useQaToggleFeature } from 'Shared/hooks';
import { useQuoteApi } from 'Shared/services/Api';
import {
  ScreenHeader,
  Footer,
  FooterNextButton,
  FooterPreviousButton,
  ReportDetails,
  Body,
  BlockNavPrompt,
} from 'QuoteApp/components';
import {
  ReportsProvider,
  useAppContext,
  useReportsContext,
} from 'QuoteApp/services/providers';

import {
  transformDisclosuresResponse,
  GetDisclosuresResponseType,
  Disclosures,
} from 'QuoteApp/services/endpoints/GetDisclosures';
import { useFooter } from 'QuoteApp/utils/hooks';
import {
  getContactInfoByFieldValue,
  getContactInfoCompleteness,
  getDriverDataByFieldValue,
  getDriverDataCompleteness,
  getVehicleDataByFieldValue,
  getVehicleDataCompleteness,
} from 'QuoteApp/utils';

import { getDriversWithoutAssignments } from 'QuoteApp/components/VehicleDriverAssignment';
import { NcfReport } from './NcfReport';
import { CurrentCarrierReport } from './CurrentCarrierReport';
import { ClueReport } from './ClueReport';
import { MvrReport } from './MvrReport';
import { useRefreshDataOnMount } from './UseRefreshDataOnMount';
import styles from './styles.module.css';
import { RewardsBanner } from 'QuoteApp/components/RewardsBanner';
import { CoverageUpdateModal } from 'Shared/components/CoverageUpdateModal';
import { getCoverageErrors } from 'QuoteApp/services/providers/AppProvider/hooks';
import { isBlank } from 'Shared/utils';

export const Reports: FC = () => {
  const { previousStep, nextStep } = useFooter(4);
  const disclosureRef = useRef(null);

  const history = useHistory();
  const {
    config,
    details,
    appId,
    canNavigate,
    rewardsEnrollmentResponse,
  } = useAppContext();
  const { loading } = useRefreshDataOnMount({ force: true });

  const {
    reportsDisabled,
    acknowledgeDisclosure,
    completingDisclosure,
  } = useReportsContext();

  const [{ data: initialDisclosureData }] = useQuoteApi<
    GetDisclosuresResponseType
  >({
    url: `insurance_applications/${appId}/document_signatures/${Disclosures.Initial}`,
    transformResponse: transformDisclosuresResponse,
  });

  const [{ data: reportDisclosureData }] = useQuoteApi<
    GetDisclosuresResponseType
  >({
    url: `insurance_applications/${appId}/document_signatures/${Disclosures.Reports}`,
    transformResponse: transformDisclosuresResponse,
  });

  const serverErrors = useMemo(
    () => ({
      drivers: getDriverDataByFieldValue(details.drivers, 'errors'),
      vehicles: getVehicleDataByFieldValue(details.vehicles, 'errors'),
      contactInfo: getContactInfoByFieldValue(details.contactInfo, 'errors'),
    }),
    [details.contactInfo, details.drivers, details.vehicles]
  );

  const dataIncomplete = useMemo(
    () =>
      Object.values({
        ...getDriverDataCompleteness(serverErrors.drivers),
        ...getVehicleDataCompleteness(serverErrors.vehicles),
        ...getContactInfoCompleteness(serverErrors.contactInfo),
        isAddressValid:
          details.addressOutcomes.isAddressValid &&
          !details.addressOutcomes.isMissingLineTwo,
        isVehicleDriverAssignmentComplete:
          details.vehicleDriverAssignment === null ||
          getDriversWithoutAssignments(
            details.drivers,
            details.vehicleDriverAssignment
          ).length === 0,
      }).some(v => !v),
    [details, serverErrors]
  );

  const coverageErrors = getCoverageErrors(details.coverages);

  const hasCoverageValidationErrors =
    !isBlank(coverageErrors.policy) ||
    Object.values(coverageErrors.vehicles).some(errors => !isBlank(errors));

  const validateAdasScreenRequired = () => {
    return Object.values(details.vehicles).some(({ needsAdas }) => needsAdas);
  };

  const [adasFeatureFlag] = useQaToggleFeature('adas_features');
  const handleNextStep = () => {
    const isAdasScreenRequired = validateAdasScreenRequired();
    return isAdasScreenRequired && adasFeatureFlag
      ? history.push(
          urlJoin(QUOTE_APP_ROUTE, appId, 'advanced_driver_assistance')
        )
      : nextStep.onClickSuccess();
  };

  if (!config || loading) {
    return <LoadingPage />;
  }

  return (
    <>
      <Body data-testid="reports-page">
        <BlockNavPrompt
          when={!canNavigate && details.status !== 'stop'}
          header="Reports in progress"
          content="Please wait for third party reports to complete."
        />
        {rewardsEnrollmentResponse?.isEnrolled && <RewardsBanner />}
        <ScreenHeader headerText="Reports" />
        <CSSTransition
          nodeRef={disclosureRef}
          unmountOnExit
          in={reportsDisabled}
          timeout={400}
          classNames={styles}
        >
          <div ref={disclosureRef} className={styles.highlightSection}>
            <h3>Disclosure Notice</h3>
            <div
              className={styles.disclosure}
              dangerouslySetInnerHTML={initialDisclosureData?.bodyTextSanitized}
            />
            <div className={styles.disclosureActions}>
              <Button
                link
                type="button"
                className={styles.linkButton}
                data-testid="reports-disclosure-return-button"
                onClick={previousStep.onClickSuccess}
              >
                Return to Summary
              </Button>
              <Button
                type="button"
                variant="primary"
                data-testid="reports-disclosure-continue-button"
                isSubmitting={completingDisclosure}
                onClick={() =>
                  acknowledgeDisclosure(Disclosures.Initial, false)
                }
              >
                Continue
              </Button>
            </div>
          </div>
        </CSSTransition>

        <div
          className={classNames(styles.container, {
            [styles.disabledReports]: reportsDisabled,
          })}
        >
          <h3>Driver & Vehicle Reports</h3>
          <div
            className={styles.subHeader}
            dangerouslySetInnerHTML={reportDisclosureData?.bodyTextSanitized}
          />
          {(dataIncomplete || hasCoverageValidationErrors) && (
            <Alert
              type="info"
              message="Application data is incomplete. You may not be able to run some reports."
            />
          )}
          <div className={styles.wrapper}>
            <div className={styles.header}>
              <Row>
                <Column variant="threeFourths">
                  <div className={styles.headerItem}>Reports</div>
                </Column>
              </Row>
            </div>
          </div>
          <CurrentCarrierReport />
          <NcfReport />
          <ClueReport />
          <MvrReport />
        </div>
        <ReportDetails />
        <CoverageUpdateModal
          open={details.quoteChangeAcknowledgement}
          redirectToSummary
        />
      </Body>
      <Footer
        nextButton={
          <FooterNextButton
            onClick={handleNextStep}
            disabled={!details.commands.bind.runnable || !canNavigate}
          >
            {nextStep.text}
          </FooterNextButton>
        }
        previousButton={
          <FooterPreviousButton
            onClick={previousStep.onClickSuccess}
            disabled={!canNavigate}
          >
            {previousStep.text}
          </FooterPreviousButton>
        }
        showRewardsEarned={true}
      />
    </>
  );
};

export const ReportsWrapper: FC = () => (
  <ReportsProvider>{() => <Reports />}</ReportsProvider>
);
